import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, AlertTriangle, ChevronDown, ChevronRight, ExternalLink, FileDown, FileSpreadsheet, Layers, LayoutGrid, X,
} from 'lucide-react';

const SEV_CHIP = {
  critical: 'bg-rose-600 text-white', high: 'bg-orange-500 text-white',
  medium: 'bg-amber-400 text-amber-950', low: 'bg-slate-300 text-slate-700',
};
const SEV_CELL = {
  critical: 'bg-rose-100 text-rose-700', high: 'bg-orange-100 text-orange-700',
  medium: 'bg-amber-100 text-amber-800', low: 'bg-slate-100 text-slate-600',
};
const STATE_CHIP = {
  open: ['Open', 'bg-slate-200 text-slate-700'], in_progress: ['Being fixed', 'bg-blue-50 text-blue-700'],
  fixed: ['Fixed', 'bg-emerald-50 text-emerald-700'], accepted: ['Accepted as-is', 'bg-emerald-50 text-emerald-700'],
};
const when = v => (v ? new Date(v).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const keyOf = i => `${i.audit_id}|${i.fid}`;

const Stat = ({ label, value, cls = 'text-slate-800', testid }) => (
  <div className="bg-white border border-slate-200 rounded-md px-4 py-3" data-testid={testid}>
    <div className={`text-2xl font-bold ${cls}`}>{value ?? '—'}</div>
    <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mt-0.5">{label}</div>
  </div>
);

const RefreshLine = ({ job, onRun }) => {
  const running = job?.status === 'running';
  return (
    <div className="flex items-center gap-3 flex-wrap text-xs text-slate-500" data-testid="qa-board-refresh-status">
      {running
        ? <span className="inline-flex items-center gap-1.5 text-blue-700"><Loader2 className="w-3.5 h-3.5 animate-spin" />
            Re-auditing every course… {job.done || 0} of {job.total || '…'} done{job.current ? ` (now: ${job.current})` : ''}</span>
        : job?.finished_at
          ? <span>All courses re-audited {when(job.finished_at)} by {job.by}
              {(job.failed || []).length ? ` · ${job.failed.length} could not be audited` : ''}</span>
          : <span>Showing the latest audit of each course.</span>}
      <Button size="sm" variant="outline" onClick={onRun} disabled={running} className="h-7 rounded-md text-xs" data-testid="qa-board-reaudit">
        <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Re-audit all now
      </Button>
    </div>
  );
};

const TypeGroup = ({ t, items, open, onToggle, selected, onSelect, onSelectMany, onOpen }) => {
  const keys = items.map(keyOf);
  const all = keys.length > 0 && keys.every(k => selected.has(k));
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={`qa-board-type-${t.rule}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <input type="checkbox" checked={all} onChange={() => onSelectMany(keys, !all)} className="w-4 h-4 accent-slate-800"
          aria-label={`Select every ${t.name} issue`} data-testid={`qa-board-select-type-${t.rule}`} />
        <button onClick={onToggle} className="flex-1 min-w-0 flex items-center gap-3 text-left">
          <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full shrink-0 ${SEV_CHIP[t.severity]}`}>{t.severity}</span>
          <span className="min-w-0">
            <span className="block text-sm font-bold text-slate-800 truncate">{t.name}</span>
            <span className="block text-[11px] text-slate-400">Rule {t.rule}{t.group_name ? ` · ${t.group_name}` : ''}
              <a href={`/course-qa/rules#rule-${t.rule}`} onClick={e => e.stopPropagation()} className="ml-2 text-blue-600 hover:underline">what is this?</a></span>
          </span>
          <span className="ml-auto text-xs text-slate-500 shrink-0 text-right">
            <span className="font-semibold text-slate-700">{t.courses} course{t.courses === 1 ? '' : 's'}</span>
            <span className="block text-[10px]">{t.open} open{t.in_progress ? ` · ${t.in_progress} being fixed` : ''}</span>
          </span>
          {open ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-slate-100 overflow-x-auto">
          <table className="w-full text-left min-w-[720px]">
            <tbody>
              {items.map(i => {
                const [word, cls] = STATE_CHIP[i.state] || STATE_CHIP.open;
                return (
                  <tr key={keyOf(i)} className="border-b border-slate-50 last:border-0 align-top hover:bg-slate-50/60" data-testid={`qa-board-row-${i.slug}-${i.fid}`}>
                    <td className="pl-4 pr-2 py-2.5 w-8">
                      <input type="checkbox" checked={selected.has(keyOf(i))} onChange={() => onSelect(keyOf(i))}
                        className="w-4 h-4 accent-slate-800" data-testid={`qa-board-select-${i.slug}-${i.fid}`} />
                    </td>
                    <td className="px-2 py-2.5 w-52 text-xs font-semibold text-slate-800">{i.title}
                      <span className="block text-[11px] font-normal text-slate-500">{i.section} · {i.where}</span>
                      {i.section_type && <span className="block text-[9px] font-mono font-normal text-slate-400">{i.section_type}</span>}
                    </td>
                    <td className="px-2 py-2.5 text-xs text-slate-700">{i.issue}
                      {i.fix && <span className="block text-[11px] text-slate-500 mt-0.5"><b>Fix:</b> {i.fix}</span>}
                    </td>
                    <td className="px-2 py-2.5 w-32">
                      <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${cls}`}>{word}</span>
                      {i.by && i.state !== 'open' && <span className="block text-[10px] text-slate-400 mt-0.5">{i.by} · {when(i.at)}</span>}
                    </td>
                    <td className="px-3 py-2.5 w-20 text-right">
                      <button onClick={() => onOpen(i.audit_id)} className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                        data-testid={`qa-board-open-${i.slug}-${i.fid}`}>Open <ExternalLink className="w-3 h-3" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const CourseGrid = ({ data, onOpen }) => (
  <div className="bg-white border border-slate-200 rounded-md overflow-x-auto" data-testid="qa-board-grid">
    <table className="text-left">
      <thead>
        <tr className="border-b border-slate-200">
          <th className="px-4 py-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold sticky left-0 bg-white min-w-[220px]">Course</th>
          <th className="px-2 py-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold text-center">Total</th>
          {data.types.map(t => (
            <th key={t.rule} title={t.name} className="px-1 py-2 align-bottom">
              <div className="w-16 text-[10px] leading-tight font-semibold text-slate-600 line-clamp-3">{t.name}</div>
              <div className={`mt-1 h-1 rounded-full ${SEV_CHIP[t.severity].split(' ')[0]}`} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.courses.map(c => (
          <tr key={c.slug} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`qa-board-grid-row-${c.slug}`}>
            <td className="px-4 py-2 sticky left-0 bg-white">
              <button onClick={() => onOpen(c.audit_id)} className="text-xs font-semibold text-slate-800 hover:text-blue-700 text-left">{c.title}</button>
              <span className="block text-[10px] text-slate-400">audited {when(c.audited_at)}</span>
            </td>
            <td className="px-2 py-2 text-center text-sm font-bold text-slate-800">{c.total}</td>
            {data.types.map(t => {
              const n = c.by_type[t.rule] || 0;
              return (
                <td key={t.rule} className="px-1 py-2 text-center">
                  {n ? <button onClick={() => onOpen(c.audit_id)} title={`${c.title}: ${t.name}`}
                         className={`w-9 h-7 rounded text-xs font-bold ${SEV_CELL[t.severity]} hover:ring-2 hover:ring-slate-300`}>{n}</button>
                     : <span className="text-slate-200 text-xs">·</span>}
                </td>
              );
            })}
          </tr>
        ))}
        {data.not_audited.map(c => (
          <tr key={c.slug} className="border-b border-slate-50">
            <td className="px-4 py-2 sticky left-0 bg-white text-xs text-slate-500">{c.title}
              <span className="block text-[10px] text-amber-700">not audited yet</span></td>
            <td colSpan={data.types.length + 1} />
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ReopenedAlerts = ({ alerts, onDismiss, onOpen }) => (
  <div className="space-y-2" data-testid="qa-board-alerts">
    {alerts.map(a => (
      <div key={a.id} className="bg-amber-50 border border-amber-300 rounded-md px-4 py-3" data-testid={`qa-board-alert-${a.id}`}>
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900">
              {a.items.length} issue{a.items.length === 1 ? ' was' : 's were'} marked fixed on {a.title}, but the re-audit on {when(a.at)} found {a.items.length === 1 ? 'it' : 'them'} still failing — reopened.
            </p>
            <ul className="mt-1.5 space-y-0.5">
              {a.items.map(i => (
                <li key={i.fid} className="text-xs text-amber-900/80">
                  <span className="font-semibold">{i.section}:</span> {i.issue}
                  {i.fixed_by && <span className="text-amber-700/70"> · marked fixed by {i.fixed_by}{i.fixed_at ? ` on ${when(i.fixed_at)}` : ''}</span>}
                </li>
              ))}
            </ul>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button size="sm" variant="outline" onClick={() => onOpen(a.audit_id)} className="h-7 rounded-md text-xs bg-white" data-testid={`qa-board-alert-open-${a.id}`}>Open course</Button>
            <Button size="sm" onClick={() => onDismiss(a.id)} className="h-7 rounded-md text-xs bg-amber-600 hover:bg-amber-700" data-testid={`qa-board-alert-dismiss-${a.id}`}>Dismiss</Button>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const BatchBar = ({ count, note, setNote, onApply, busy, onClear }) => (
  <div className="sticky bottom-4 z-20 mx-auto max-w-3xl bg-slate-900 text-white rounded-lg shadow-xl px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="qa-board-batch-bar">
    <span className="text-sm font-semibold" data-testid="qa-board-selected-count">{count} selected</span>
    <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Note for all (optional)"
      className="h-8 flex-1 min-w-[160px] bg-slate-800 border-slate-700 text-white text-xs rounded-md" data-testid="qa-board-batch-note" />
    <Button size="sm" variant="outline" disabled={busy} onClick={() => onApply('open')} className="h-8 rounded-md bg-transparent border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white text-xs" data-testid="qa-board-batch-reopen">Reopen</Button>
    <Button size="sm" disabled={busy} onClick={() => onApply('in_progress')} className="h-8 rounded-md bg-blue-600 hover:bg-blue-700 text-xs" data-testid="qa-board-batch-in-progress">Mark being fixed</Button>
    <Button size="sm" disabled={busy} onClick={() => onApply('fixed')} className="h-8 rounded-md bg-emerald-600 hover:bg-emerald-700 text-xs" data-testid="qa-board-batch-fixed">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Mark fixed'}</Button>
    <button onClick={onClear} className="text-slate-400 hover:text-white" aria-label="Clear selection" data-testid="qa-board-batch-clear"><X className="w-4 h-4" /></button>
  </div>
);

export default function CourseQaDefectsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('types');
  const [filters, setFilters] = useState({ severity: '', group: '', status: '', q: '' });
  const [openTypes, setOpenTypes] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [search, setSearch] = useState('');
  const started = useRef(false);

  const load = useCallback(() => {
    setLoading(true);
    return api.get('/course-qa/defects', { params: filters })
      .then(r => { setData(r.data); setJob(r.data.refresh); })
      .catch(e => toast.error(e.response?.data?.detail || 'Could not load the defect board'))
      .finally(() => setLoading(false));
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const startRefresh = useCallback(async (force) => {
    try {
      const r = await api.post('/course-qa/defects/refresh', { force });
      setJob(r.data);
      if (force && r.data.status === 'running') toast.success('Re-auditing every course — the board updates when it finishes');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start the re-audit'); }
  }, []);
  useEffect(() => { if (!started.current) { started.current = true; startRefresh(false); } }, [startRefresh]);
  useEffect(() => {
    if (job?.status !== 'running') return undefined;
    const t = setInterval(() => api.get('/course-qa/defects/refresh').then(r => {
      setJob(r.data);
      if (r.data.status !== 'running') { load(); toast.success('Every course re-audited — board updated'); }
    }).catch(() => {}), 3000);
    return () => clearInterval(t);
  }, [job?.status, load]);

  const toggle = k => setSelected(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const selectMany = (keys, on) => setSelected(s => { const n = new Set(s); keys.forEach(k => (on ? n.add(k) : n.delete(k))); return n; });

  const applyBatch = async (state) => {
    setBusy(true);
    try {
      const items = [...selected].map(k => { const [audit_id, fid] = k.split('|'); return { audit_id, fid }; });
      const r = await api.post('/course-qa/defects/bulk', { items, state, note });
      toast.success(`${r.data.updated} issue(s) ${state === 'open' ? 'reopened' : state === 'fixed' ? 'marked fixed' : 'marked being fixed'}${r.data.failed.length ? ` · ${r.data.failed.length} could not be changed` : ''}`);
      setSelected(new Set()); setNote(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update those issues'); } finally { setBusy(false); }
  };

  const download = async (kind) => {
    try {
      const r = await api.get(`/course-qa/defects/${kind}`, { params: filters, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `course-qa-defects.${kind}`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error('Download failed'); }
  };

  useEffect(() => {
    const t = setTimeout(() => setFilters(f => (f.q === search ? f : { ...f, q: search })), 350);
    return () => clearTimeout(t);
  }, [search]);
  const dismissAlert = async id => {
    try { await api.post(`/course-qa/defects/alerts/${id}/dismiss`); toast.success('Alert dismissed'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not dismiss'); }
  };
  const setF = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setSelected(new Set()); };
  const openAudit = id => navigate(`/course-qa?audit=${id}`);
  const navTab = (to, label) => (
    <button key={to} onClick={() => navigate(to)}
      className={`text-xs font-medium px-2.5 py-1 rounded-sm transition-colors ${location.pathname === to ? 'text-white' : 'text-slate-300 hover:text-white'}`}
      data-testid={`qa-nav-${label.toLowerCase().replace(/ /g, '-')}`}>{label}</button>
  );
  const t = data?.totals || {};
  const select = 'h-8 text-xs border border-slate-200 rounded-md px-2 bg-white';

  return (
    <div className="-m-4 sm:-m-6" data-testid="course-qa-defects-page">
      <div className="bg-[#0f2b4c] px-4 sm:px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-baseline gap-3">
          <span className="text-white text-base sm:text-lg font-bold tracking-tight">Course Page QA</span>
          <span className="text-slate-300 text-xs hidden sm:inline">Defect board — every open issue, every course</span>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {navTab('/course-qa', 'Audit a page')}
          {navTab('/course-qa/all', 'Audit all courses')}
          {navTab('/course-qa/defects', 'Defect board')}
          {navTab('/course-qa/rules', 'Rules guide')}
          {user?.role !== 'course_qa' && navTab('/course-qa/management', 'Management')}
        </div>
      </div>

      <div className="p-4 sm:p-6 bg-slate-50 min-h-[calc(100vh-7rem)] space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-lg font-bold text-slate-800">Defect board</h1>
            <p className="text-xs text-slate-400 mt-0.5 mb-2">Issues still open on the latest audit of each course, grouped so the CMS team can fix the same problem everywhere in one go.</p>
            <RefreshLine job={job} onRun={() => startRefresh(true)} />
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => download('csv')} className="rounded-md h-8 text-xs" data-testid="qa-board-export-csv">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Spreadsheet (CSV)</Button>
            <Button size="sm" variant="outline" onClick={() => download('pdf')} className="rounded-md h-8 text-xs" data-testid="qa-board-export-pdf">
              <FileDown className="w-3.5 h-3.5 mr-1.5" /> PDF</Button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <Stat label="Issues" value={t.issues} testid="qa-board-stat-issues" />
          <Stat label="Courses affected" value={data ? `${t.courses_affected}/${t.courses_audited}` : null} testid="qa-board-stat-courses" />
          <Stat label="Issue types" value={t.types} testid="qa-board-stat-types" />
          <Stat label="Critical" value={t.critical} cls="text-rose-600" testid="qa-board-stat-critical" />
          <Stat label="High" value={t.high} cls="text-orange-500" testid="qa-board-stat-high" />
          <Stat label="Medium / low" value={data ? `${t.medium} / ${t.low}` : null} cls="text-amber-500" testid="qa-board-stat-medium" />
        </div>

        {(data?.alerts || []).length > 0 && <ReopenedAlerts alerts={data.alerts} onDismiss={dismissAlert} onOpen={openAudit} />}

        {(data?.not_audited || []).length > 0 && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="qa-board-not-audited">
            {data.not_audited.length} course(s) have not been audited yet and are not on the board: {data.not_audited.map(c => c.title).join(', ')}.
          </p>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 mr-auto" data-testid="qa-board-tabs">
            {[['types', 'By issue type', Layers], ['courses', 'By course', LayoutGrid]].map(([v, label, Icon]) => (
              <button key={v} onClick={() => setTab(v)} data-testid={`qa-board-tab-${v}`}
                className={`text-xs px-3 py-1 rounded inline-flex items-center gap-1.5 transition-colors ${tab === v ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                <Icon className="w-3.5 h-3.5" /> {label}</button>
            ))}
          </div>
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search course or issue…" className="h-8 w-56 rounded-md text-xs" data-testid="qa-board-search" />
          <select value={filters.severity} onChange={e => setF('severity', e.target.value)} className={select} data-testid="qa-board-severity">
            <option value="">Any severity</option>
            {['critical', 'high', 'medium', 'low'].map(s => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
          </select>
          <select value={filters.group} onChange={e => setF('group', e.target.value)} className={select} data-testid="qa-board-group">
            <option value="">Every area</option>
            {(data?.groups || []).map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <select value={filters.status} onChange={e => setF('status', e.target.value)} className={select} data-testid="qa-board-status">
            <option value="">Open + being fixed</option>
            <option value="open">Open only</option>
            <option value="in_progress">Being fixed only</option>
            <option value="resolved">Resolved (fixed / accepted)</option>
            <option value="all">Everything</option>
          </select>
        </div>

        {loading && !data && <div className="p-8 text-center text-xs text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading the board…</div>}
        {data && data.types.length === 0 && (
          <div className="bg-white border border-emerald-200 rounded-md p-6 text-sm text-emerald-800" data-testid="qa-board-empty">No issues match these filters.</div>
        )}
        {data && tab === 'types' && (
          <div className="space-y-3" data-testid="qa-board-types">
            {data.types.map((ty, idx) => (
              <TypeGroup key={ty.rule} t={ty} items={data.issues.filter(i => i.rule === ty.rule)}
                open={openTypes[ty.rule] ?? idx < 3} onToggle={() => setOpenTypes(o => ({ ...o, [ty.rule]: !(o[ty.rule] ?? idx < 3) }))}
                selected={selected} onSelect={toggle} onSelectMany={selectMany} onOpen={openAudit} />
            ))}
          </div>
        )}
        {data && tab === 'courses' && data.types.length > 0 && <CourseGrid data={data} onOpen={openAudit} />}

        {selected.size > 0 && (
          <BatchBar count={selected.size} note={note} setNote={setNote} onApply={applyBatch} busy={busy} onClear={() => setSelected(new Set())} />
        )}
      </div>
    </div>
  );
}
